import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as XLSX from 'xlsx';
import axiosClient from 'api/shared/AxiosClient';
import AnalyticsService from 'api/services/AnalyticsService';
import { getDateRangeString } from 'utils/Utils';
import { ALL_SECTIONS } from 'pages/dashboard/analytics/ExportSectionPickerModal';

const conditionalGet = (condition, url) =>
    condition ? axiosClient.get(url) : Promise.resolve(null);

const makeExportHelpers = (t) => ({
    col:   (key) => t(`analytics.export.columns.${key}`),
    sheet: (key) => t(`analytics.export.sheets.${key}`),
});

const useAnalyticsExport = ({ dateRanges, channel, language }) => {
    const { t } = useTranslation();
    const [isExporting, setIsExporting] = useState(false);

    const exportReport = async (sections = ALL_SECTIONS) => {
        setIsExporting(true);
        try {
            const kpi = sections.includes('kpiSection');
            const cq  = sections.includes('conversationQuality');
            const [
                overviewRes,
                conversationsRes,
                messagesRes,
                servicesRes,
                typesRes,
                languagesRes,
                odRes,
                uniqueUsersRes,
                escalationRes,
                topIntentsRes,
                cqRes,
            ] = await Promise.all([
                conditionalGet(sections.includes('overview'),           AnalyticsService.getOverview(dateRanges, channel, language).url),
                conditionalGet(sections.includes('conversationsPerDay'),AnalyticsService.getConverstationsPerDay(dateRanges, channel, language).url),
                conditionalGet(sections.includes('messagesPerDay'),     AnalyticsService.getMessagesPerDay(dateRanges, channel, language).url),
                conditionalGet(sections.includes('byService'),          AnalyticsService.getConversationsByService(dateRanges, channel, language).url),
                conditionalGet(sections.includes('byType'),             AnalyticsService.getMessagesByType(dateRanges, channel, language).url),
                conditionalGet(sections.includes('byLanguage'),         AnalyticsService.getMessagesByLanguage(dateRanges, channel, language).url),
                conditionalGet(sections.includes('od'),                 AnalyticsService.getOriginDestinaion(dateRanges, channel, language).url),
                conditionalGet(kpi,                                     AnalyticsService.getUniqueUsers(dateRanges, channel, language).url),
                conditionalGet(kpi,                                     AnalyticsService.getEscalationRate(dateRanges, channel, language).url),
                conditionalGet(kpi,                                     AnalyticsService.getTopIntents(dateRanges, channel, language).url),
                conditionalGet(cq,                                      AnalyticsService.getConversationQuality(dateRanges, channel, language).url),
            ]);

            // Column header helpers
            const { col, sheet } = makeExportHelpers(t);

            const wb = XLSX.utils.book_new();

            // ── Sheet 1 — KPI Overview ──────────────────────────────────────
            if (overviewRes) {
                const overview = overviewRes.data;
                const sheet1 = XLSX.utils.json_to_sheet([
                    { [col('metric')]: t('analytics.overview.length.title'),               [col('value')]: overview.length                },
                    { [col('metric')]: t('analytics.overview.total_users.title'),          [col('value')]: overview.total_users           },
                    { [col('metric')]: t('analytics.overview.handled_requests.title'),     [col('value')]: overview.handled_requests       },
                    { [col('metric')]: t('analytics.overview.rejected_requests.title'),    [col('value')]: overview.rejected_requests      },
                    { [col('metric')]: t('analytics.overview.engaged_users.title'),        [col('value')]: overview.engaged_users          },
                    { [col('metric')]: t('analytics.overview.retention_rate.title'),       [col('value')]: overview.retention_rate         },
                    { [col('metric')]: t('analytics.overview.avg_messages_per_user.title'),[col('value')]: overview.avg_messages_per_user  },
                ]);
                XLSX.utils.book_append_sheet(wb, sheet1, sheet('kpi_overview'));
            }

            // ── Sheet 2 — Conversations Per Day ─────────────────────────────
            if (conversationsRes) {
                const convPerDay = conversationsRes.data;
                const sheet2 = XLSX.utils.json_to_sheet(
                    (convPerDay.date || []).map((date, i) => ({
                        [col('date')]: date,
                        [t('analytics.conversations_per_day.title')]: convPerDay.total_conversation?.[i] ?? 0,
                    }))
                );
                XLSX.utils.book_append_sheet(wb, sheet2, sheet('conversations_per_day'));
            }

            // ── Sheet 3 — Messages Per Day ───────────────────────────────────
            if (messagesRes) {
                const msgsPerDay = messagesRes.data;
                const sheet3 = XLSX.utils.json_to_sheet(
                    (msgsPerDay.date || []).map((date, i) => ({
                        [col('date')]: date,
                        [t('analytics.messages_per_day.handled_messages')]:   msgsPerDay.handled_messages?.[i]   ?? 0,
                        [t('analytics.messages_per_day.unhandled_messages')]: msgsPerDay.unhandled_messages?.[i] ?? 0,
                    }))
                );
                XLSX.utils.book_append_sheet(wb, sheet3, sheet('messages_per_day'));
            }

            // ── Sheet 4 — Conversations By Service ───────────────────────────
            if (servicesRes) {
                const services = servicesRes.data;
                const serviceKeys = [
                    'horaire_tarif', 'cancel_exchange', 'train_auto',
                    'reclamation', 'astuce', 'gare_proche', 'tarif_reduit',
                ];
                const sheet4 = XLSX.utils.json_to_sheet(
                    serviceKeys.map((key) => ({
                        [col('service')]: t(`analytics.conversations_by_service.${key}.title`).replace(/^#/, ''),
                        [col('count')]:   services[key] ?? 0,
                    }))
                );
                XLSX.utils.book_append_sheet(wb, sheet4, sheet('by_service'));
            }

            // ── Sheet 5 — Messages By Type ───────────────────────────────────
            if (typesRes) {
                const types = typesRes.data;
                const sheet5 = XLSX.utils.json_to_sheet([
                    { [col('type')]: t('analytics.messages_by_type.text.title'),  [col('count')]: types.text  ?? 0 },
                    { [col('type')]: t('analytics.messages_by_type.voice.title'), [col('count')]: types.voice ?? 0 },
                ]);
                XLSX.utils.book_append_sheet(wb, sheet5, sheet('messages_by_type'));
            }

            // ── Sheet 6 — Messages By Language ───────────────────────────────
            if (languagesRes) {
                const langs = languagesRes.data;
                const langKeys = ['french', 'arabic', 'arabic_dialect_ma', 'english'];
                const sheet6 = XLSX.utils.json_to_sheet(
                    langKeys.map((key) => ({
                        [col('language')]: t(`analytics.messages_by_language.${key}.title`),
                        [col('count')]:    langs[key] ?? 0,
                    }))
                );
                XLSX.utils.book_append_sheet(wb, sheet6, sheet('messages_by_language'));
            }

            // ── Sheet 7 — Top 20 OD Pairs ────────────────────────────────────
            if (odRes) {
                const od = odRes.data;
                const sheet7 = XLSX.utils.json_to_sheet(
                    (od.od || []).map((pair, i) => ({
                        [col('origin_destination')]: pair,
                        [col('count')]:              od.counts?.[i] ?? 0,
                    }))
                );
                XLSX.utils.book_append_sheet(wb, sheet7, sheet('top_20_od'));
            }

            // ── Sheet 8 — KPI Section ────────────────────────────────────────
            if ([uniqueUsersRes, escalationRes, topIntentsRes].every(Boolean)) {
                const uniqueUsers = uniqueUsersRes.data;
                const escalation  = escalationRes.data;
                const topIntents  = topIntentsRes.data;
                const intents = topIntents.intents || [];
                const sheet8 = XLSX.utils.aoa_to_sheet([
                    [col('metric'),                             col('value')],
                    [t('analytics.kpi.unique_users.title'),    uniqueUsers.unique_users ?? 0],
                    [t('analytics.kpi.escalation_rate.title'), `${escalation.escalation_rate ?? 0}%`],
                    [],
                    [col('rank'), col('label_fr'), col('label_en'), col('count')],
                    ...intents.map((item, idx) => [idx + 1, item.labelFr, item.labelEn, item.count]),
                ]);
                XLSX.utils.book_append_sheet(wb, sheet8, sheet('kpi_section'));
            }

            // ── Sheet 9 — Conversation Quality ──────────────────────────────
            if (cqRes) {
                const cqData = cqRes.data;
                const CQ_KEYS = [
                    'total_flows', 'resolved_flows', 'unresolved_flows', 'abandoned_flows',
                    'escalated_flows', 'resolution_rate', 'avg_resolution_time', 'fcr_rate',
                ];
                const sheet9 = XLSX.utils.json_to_sheet(
                    CQ_KEYS.map((key) => ({
                        [col('metric')]: t(`analytics.conversationQuality.${key}.title`),
                        [col('value')]:  cqData[key] ?? '—',
                    }))
                );
                XLSX.utils.book_append_sheet(wb, sheet9, sheet('conversationQuality'));
            }

            XLSX.writeFile(wb, `MONCF_Analytics_${getDateRangeString(dateRanges)}.xlsx`);
        } catch (err) {
            console.error('[useAnalyticsExport]', err);
        } finally {
            setIsExporting(false);
        }
    };

    return { exportReport, isExporting };
};

export default useAnalyticsExport;
